import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import { ReactComponent as IconExport } from "assets/icon_export.svg";
import NoDataPlaceholder from "components/no-data-placeholder/NoDataPlaceholder";
import SearchInput from "components/search-input/SearchInput";
import { CandidatesTableHead } from "components/table-head/CandidatesTableHead";
import { MatchTableHead } from "components/table-head/MatchTableHead";
import { NoMatchTableHead } from "components/table-head/NoMatchTableHead";
import { CandidatesTableRow } from "components/table-row/CandidatesTableRow";
import { MatchTableRow } from "components/table-row/MatchTableRow";
import { NoMatchTableRow } from "components/table-row/NoMatchTableRow";
import { downloadItem } from "containers/download/download-item";
import { AppContext } from "helpers/context";
import { Item } from "helpers/models";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  readonly data: any[];
  readonly type?: "candidates" | "no_match" | "match";
  readonly handleMove: (id: string, type?: string) => void;
  readonly handleData: (data: any) => void;
  readonly isShowBtn?: boolean;
  readonly setSelectOnMap?: (item: any) => void;
  readonly onRowClick?: (item: any) => void;
  readonly selectedId?: string | null;
}

export default function TableView(props: Props) {
  const {
    data: propData,
    type,
    handleMove,
    handleData,
    isShowBtn,
    setSelectOnMap,
    onRowClick,
    selectedId
  } = props;
  const [context] = useContext(AppContext);
  const [t] = useTranslation();

  const tableComponents = useMemo(
    () => ({
      candidates: { Head: CandidatesTableHead, Row: CandidatesTableRow },
      match: { Head: MatchTableHead, Row: MatchTableRow },
      no_match: { Head: NoMatchTableHead, Row: NoMatchTableRow }
    }),
    []
  );

  const TableHeadComponent = type ? tableComponents[type]?.Head : null;
  const TableRowComponent = type ? tableComponents[type]?.Row : null;

  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeSort, setActiveSort] = useState<{
    property: "name" | "address" | "quality" | "aggregationUnit" | "currency";
    direction: "asc" | "desc";
  }>({ property: "name", direction: "asc" });
  const [shouldJumpToPage, setShouldJumpToPage] = useState(false);

  const handleSort = (property: "name" | "address" | "quality" | "aggregationUnit" | "currency") => {
    const isAsc =
      activeSort.property === property && activeSort.direction === "asc";
    setActiveSort({ property, direction: isAsc ? "desc" : "asc" });
  };

  const filteredData = useMemo(() => {
    if (!inputValue) {
      return propData || [];
    }
    return (propData || []).filter((item) => {
      if (
        item.name &&
        item.name.toLowerCase().includes(inputValue.toLowerCase())
      ) {
        return true;
      }
      if (
        item.address &&
        item.address.toLowerCase().includes(inputValue.toLowerCase())
      ) {
        return true;
      }
      return false;
    });
  }, [propData, inputValue]);

  const aggregationUnit = context.importMultipleLocations?.aggregationUnit ?? "";
  const currency = context.importMultipleLocations?.currency ?? "";

  const sortedData = useMemo(() => {
    const { property, direction } = activeSort;
    return [...filteredData].sort((a, b) => {
      const prop = property === "aggregationUnit" ? aggregationUnit : property;
      if (a[prop] < b[prop]) {
        return direction === "asc" ? -1 : 1;
      }
      if (a[prop] > b[prop]) {
        return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, activeSort, aggregationUnit]);

  const currentData = useMemo(
    () =>
      sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [inputValue]);

  useEffect(() => {
    if (selectedId) {
      setShouldJumpToPage(true);
    }
  }, [selectedId]);

  useEffect(() => {
    if (selectedId && shouldJumpToPage) {
      const selectedIndex = sortedData.findIndex(
        (item) => item && item.id === selectedId
      );
      if (selectedIndex !== -1) {
        const newPage = Math.floor(selectedIndex / rowsPerPage);
        if (page !== newPage) {
          setPage(newPage);
        }
        setShouldJumpToPage(false);
      }
    }
  }, [selectedId, sortedData, rowsPerPage, shouldJumpToPage, page]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadExcel = () => {
    const name = context.importMultipleLocations?.name;
    if (!name || !propData || propData.length === 0) {
      return;
    }
    if (context.importMultipleLocations) {
      downloadItem(propData, context.importMultipleLocations, t);
    }
  }

  const editItem = (item: Item) => {
    const updatedData = propData.map((e) =>
      e.id === item.id || e.code === item.code ? { ...e, ...item } : e
    );
    handleData(updatedData);
  };

  return (
    <>
      <SearchInput value={inputValue} onChange={setInputValue} />
      {!sortedData || sortedData.length === 0 ? (
        <NoDataPlaceholder text={t("addressEncoding.noResults")} />
      ) : (
        <Box
          sx={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <TableContainer sx={{ overflowY: "auto", width: "100%", minHeight: "330px" }}>
            <Table size="small" stickyHeader aria-label="sticky table">
              {TableHeadComponent && (
                <TableHeadComponent
                  aggregationUnit={aggregationUnit}
                  activeSort={activeSort}
                  handleSort={handleSort}
                  t={t}
                />
              )}
              <TableBody>
                {currentData.map((item) => {
                  const commonProps = {
                    key: item.id,
                    aggregationUnit: aggregationUnit,
                    currency: currency,
                    item: item,
                    selectedId: selectedId,
                    setSelectOnMap: setSelectOnMap,
                    onRowClick: onRowClick,
                    handleMove: handleMove,
                    editItem: editItem,
                    t: t
                  };
                  return TableRowComponent ? (
                    <TableRowComponent {...commonProps} />
                  ) : null;
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "10px",
              alignItems: "center",
              paddingLeft: "10px"
            }}
          >
            {isShowBtn && (
              <Button
                color="success"
                startIcon={<IconExport />}
                onClick={downloadExcel}
              >
                {t("addressEncoding.table.export")}
              </Button>
            )}
            <div style={{ marginLeft: "auto" }}>
              <TablePagination
                component="div"
                count={sortedData.length}
                rowsPerPageOptions={[10, 15, 25]}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t("addressEncoding.table.rowsPerPage")}
                slotProps={{ select: { native: true } }}
                sx={{ ".MuiTablePagination-select": { textAlign: "center" } }}
              />
            </div>
          </div>
        </Box>
      )}
    </>
  );
}
